import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { Analysis } from 'app/providers/storage-api/analysis.service';
import { StorageAPIService, StorageListResult } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { SystemService } from 'app/services/system.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeService,
  RealtimeMessage,
} from '../realtime.service';

import {
  SectionMetadata,
  SectionMetadataAllReportResult,
  SectionMetadataCardinality,
} from './collection.service';
import { ObjectLabel, Sideloadable } from './object-label.service';
import { urlFor } from './utils';

export interface AnalysisGroup {
  id: string;
  name: string;
  description: string;
  projectId: string;
  createdAt: Date;
  modifiedAt: Date;
  analyses: Analysis[];
  hasTaxonomy: boolean;
  hasDateData: boolean;
  entityTypes: string[];
  topicTypes: string[];
  customFields: string[];
  standardFields: string[];
  hasEntities: boolean;
  hasPhrases: boolean;
  hasThemes: boolean;
  hasTopics: boolean;
  hasTopicMentions: boolean;
  hasTopicPhrases: boolean;
  hasTopicThemes: boolean;
  hasThemeMentions: boolean;
  hasEntityMentions: boolean;
  hasEntityPhrases: boolean;
  sourceVersion: number;
  minDocAt: Date;
  maxDocAt: Date;
  collectionQuery?: string;
  clauseModelTypes?: string[];
  queryString?: string;
  lookupId?: string;
  hasSections: boolean;
  sectionFieldNames: string[];
  notes: string;
  objectLabels?: string[] | ObjectLabel[];
  hasSectionSentiment?: boolean;
  sectionPropertyTypes?: string[];
  journeyGroupRule?: { defaultField: string; resolvedFields: string[] };
}

export interface AnalysisGroupUsages {
  dashboards: { id: string; name: string }[];
  hiddenDashboardCount: number;
}

interface JourneyGroupExceptionResponse {
  data?: string;
  description?: string;
  type?: string;
}

export type GroupSort = 'auto';
export type GroupFilter = '';

@Injectable()
export class AnalysisGroupService extends StorageAPIService {
  static map(group: AnalysisGroup, labels?: ObjectLabel[]): AnalysisGroup {
    group.createdAt = new Date(group.createdAt);
    group.modifiedAt = new Date(group.modifiedAt);
    group.minDocAt = group.minDocAt !== null ? new Date(group.minDocAt) : null;
    group.maxDocAt = group.maxDocAt !== null ? new Date(group.maxDocAt) : null;
    group.objectLabels = this.mapObjectLabels(group, labels);

    return group;
  }

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private alertService: AlertService,
    private realtimeService: RealtimeService
  ) {
    super(http, systemService);
  }

  on(namespace: string, fn: RealtimeEventHandler<AnalysisGroup>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        analysisGroup: (data: RealtimeMessage<AnalysisGroup>) => {
          AnalysisGroupService.map(data.object);
          fn(data);
        },
      },
      'analysisGroupService'
    );
  }

  getAnalysisGroups(projectId: string): Promise<StorageListResult<AnalysisGroup>> {
    if (!projectId) {
      return null;
    }
    return this.get<StorageListResult<AnalysisGroup>>(
      `/projects/${projectId}/analysis-groups`
    ).catch((error) => {
      throw error;
    });
  }

  async getAnalysisGroup(
    projectId: string,
    groupId: string,
    options?: { silent?: boolean },
    sideloads?: 'objectLabels'
  ): Promise<AnalysisGroup> {
    const params = sideloads ? { sideloads } : {};

    const result = await this.get<Sideloadable<AnalysisGroup>>(
      `/projects/${projectId}/analysis-groups/${groupId}`,
      params
    ).catch((error) => {
      if (!options?.silent) {
        this.alertService.showErrorToast(
          translate('analysis-group-service.failed-to-get-group'),
          error
        );
      }
      throw error;
    });
    return AnalysisGroupService.map(result, result.sideloaded?.objectLabels);
  }

  createAnalysisGroup(
    projectId: string,
    data: { name: string; description?: string; lookupId?: string }
  ): Promise<any> {
    return this.post<any>(`/projects/${projectId}/analysis-groups/`, data).catch((error) => {
      this.alertService.showErrorToast(
        translate('analysis-group-service.failed-to-create-group'),
        error
      );
      throw error;
    });
  }

  cloneAnalysisGroup(projectId: string, groupId: string): Promise<any> {
    return this.post<any>(`/projects/${projectId}/analysis-groups/${groupId}/clone`).catch(
      (error) => {
        this.alertService.showErrorToast(
          translate('analysis-group-service.failed-to-clone-group'),
          error
        );
        throw error;
      }
    );
  }

  updateAnalysisGroup(
    projectId: string,
    groupId: string,
    data: {
      name: string;
      description?: string;
      lookupId?: string;
      notes?: string;
    }
  ): Promise<any> {
    return this.patch<any>(`/projects/${projectId}/analysis-groups/${groupId}/`, data).catch(
      (error) => {
        this.alertService.showErrorToast(
          translate('analysis-group-service.failed-to-update-group'),
          error
        );
        throw error;
      }
    );
  }

  deleteAnalysisGroup(projectId: string, groupId: string): Promise<any> {
    return this.delete<any>(`/projects/${projectId}/analysis-groups/${groupId}/`).catch((error) => {
      this.alertService.showErrorToast(
        translate('analysis-group-service.failed-to-delete-group'),
        error
      );
      throw error;
    });
  }

  addAnalysesToAnalysisGroup(
    projectId: string,
    groupId: string,
    analysisIds: string[],
    journeyGroupDefaultField?: string
  ): Promise<any> {
    const params = journeyGroupDefaultField
      ? `?journeyGroupDefaultField=${encodeURIComponent(journeyGroupDefaultField)}`
      : '';
    return this.post<any>(
      `/projects/${projectId}/analysis-groups/${groupId}/analyses/${params}`,
      analysisIds
    ).catch((error) => {
      if (!this.isJourneyGroupExceptionError(error)) {
        this.alertService.showErrorToast(
          translate('analysis-group-service.failed-to-add-to-group')
        );
      }
      throw error;
    });
  }

  isJourneyGroupExceptionError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const errorResponse = (error as { error?: JourneyGroupExceptionResponse }).error;
    return errorResponse?.type === 'JourneyGroupException';
  }

  removeAnalysesFromAnalysisGroup(
    projectId: string,
    groupId: string,
    analysisIds: string[],
    journeyGroupDefaultField?: string
  ): Promise<any> {
    const params = journeyGroupDefaultField
      ? `?journeyGroupDefaultField=${encodeURIComponent(journeyGroupDefaultField)}`
      : '';
    return this.post<void>(
      `/projects/${projectId}/analysis-groups/${groupId}/analyses/_delete/${params}`,
      analysisIds
    ).catch((error) => {
      this.alertService.showErrorToast(
        translate('analysis-group-service.failed-to-remove-from-group'),
        error
      );
      throw error;
    });
  }

  getAnalysisGroupUsages(projectId: string, analysisGroupId: string): Promise<AnalysisGroupUsages> {
    return this.get<AnalysisGroupUsages>(
      urlFor(`/projects/${projectId}/analysis-groups/${analysisGroupId}/_usages`)
    );
  }

  getSectionMetadataCardinality(
    projectId: string,
    analysisGroupId: string
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    return this.get<StorageListResult<SectionMetadataCardinality>>(
      urlFor(
        `/projects/${projectId}/analysis-groups/${analysisGroupId}/section-metadata/_cardinality`
      )
    );
  }

  getSectionMetadataTopN(
    projectId: string,
    analysisGroupId: string,
    fieldName: string,
    bucketSize?: number
  ): Promise<SectionMetadata> {
    return this.get<SectionMetadata>(
      urlFor(`/projects/${projectId}/analysis-groups/${analysisGroupId}/section-metadata/_topn`),
      {
        'field-name': fieldName,
        'bucket-size': bucketSize,
      }
    );
  }

  getSectionMetadataAllReport(
    projectId: string,
    analysisGroupId: string,
    includes?: string[],
    excludes?: string[]
  ): Promise<SectionMetadataAllReportResult> {
    return this.get<SectionMetadataAllReportResult>(
      urlFor(`/projects/${projectId}/analysis-groups/${analysisGroupId}/section-metadata/_all`),
      {
        includes,
        excludes,
      }
    );
  }

  getSectionPropertyCardinality(
    projectId: string,
    analysisGroupId: string
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    return this.get<StorageListResult<SectionMetadataCardinality>>(
      urlFor(
        `/projects/${projectId}/analysis-groups/${analysisGroupId}/section-properties/_cardinality`
      )
    );
  }
}
